import {
  type LanguageProficiency,
  type Locale,
  scrollToError,
} from '@seek/ca-myprofile-utils';
import { createForm, type RenderParams } from '@seek/forms-ui';
import {
  ProfileAutosuggest,
  FormActionButtons,
  DeleteConfirmationDialog,
} from '@seek/profile-edit-ui';
import { rules } from '@seek/validators-js';
// This usage of useLanguage is fine because we're use the locale
// rather than the language
// eslint-disable-next-line no-restricted-imports
import { useLanguage as useVocabLanguage, useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { useCallback, useState } from 'react';

import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { useAppConfig } from 'src/modules/appConfig';
import type { OntologyStructuredData } from 'src/types';

import translations from './.vocab';
import type { LanguageUpdate } from './types';
import {
  getInitialState,
  initialValues as languageFormInitialValues,
  languageFormUpdateToLanguageUpdate,
  getStructuredData,
} from './utils';

export interface FormValues {
  language: OntologyStructuredData;
}

interface ComponentProps {
  onSubmit: (params: { dirty: boolean; values: LanguageUpdate }) => void;
  onCancel: () => void;
  onFormDirtyChange: (isDirty: boolean) => void;
  onDelete?: () => void;
  initialValues?: LanguageProficiency;
  onSubmitFail?: (errors: string[]) => void;
  scrollToTopOnMobile?: boolean;
  locale?: Locale;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const mapFormStateToProps = ({
  form: { dirty },
}: {
  form: { dirty: boolean };
}) => ({
  dirty,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;

type FormProps = RenderParams<FormValues> & MappedProps;

type LanguageFormProps = ComponentProps & FormProps;

const LANGUAGE_FORM = 'LANGUAGE_FORM';

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  LANGUAGE_FORM,
  {
    formType: 'braid',
  },
);

const Form = ({
  onSubmit,
  onCancel,
  onDelete,
  dirty,
  onFormDirtyChange,
  setFieldValue,
  handleSubmit,
  inputRef,
}: LanguageFormProps) => {
  const { t } = useTranslations(translations);
  const { locale: languageLocale } = useVocabLanguage();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useOnFormDirtyChange({ onFormDirtyChange, dirty });

  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const nameField = useField({
    id: 'language',
    ...rules.languageName({
      dataLabel: t('Language').toLocaleLowerCase(languageLocale),
    }),
  });

  const clearName = useCallback(
    () => setFieldValue('language', languageFormInitialValues.language),
    [setFieldValue],
  );

  const onSubmitFn = useCallback(
    () =>
      handleSubmit((values) => {
        onSubmit({
          dirty,
          values: languageFormUpdateToLanguageUpdate(values),
        });
      }),
    [handleSubmit, onSubmit, dirty],
  );

  const { locale } = useAppConfig();

  return (
    <form onSubmit={onSubmitFn()} autoComplete="off">
      <Stack space="large">
        <ProfileAutosuggest
          id="language"
          label={t('Language')}
          placeholder={t('e.g. English, Mandarin, German')}
          asset="SpecReq"
          queryVariables={{ profileField: 'LanguageProficiency' }}
          onClear={clearName}
          getStructuredData={getStructuredData}
          showNoResultsMessage={true}
          locale={locale}
          data={{ automation: 'language-name' }}
          inputRef={inputRef}
          {...nameField}
        />
        <FormActionButtons
          cardName={t('Language').toLocaleLowerCase(languageLocale)}
          onCancel={onCancel}
          dataAutomationSuffix="language"
          {...(onDelete && { onDeleteClick })}
        />
      </Stack>
      {onDelete && (
        <DeleteConfirmationDialog
          displayDialog={showDeleteDialog}
          setDisplayDialog={setShowDeleteDialog}
          onDelete={onDelete}
          cardName={t('Language').toLocaleLowerCase(languageLocale)}
          dataAutomationSuffix="language"
        />
      )}
    </form>
  );
};

export const LanguageForm = (props: ComponentProps) => {
  const { language } = useAppConfig();

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={getInitialState(props.initialValues)}
      onSubmitFail={
        props.onSubmitFail
          ? props.onSubmitFail
          : (errors: string[]) => scrollToError(errors)
      }
      language={language}
    >
      {(formProps) => <Form {...formProps} {...props} />}
    </FormProvider>
  );
};
